// src/lib/rfp-requirements-extractor.ts
// Extracts structured requirements with IDs from RFP analysis results

import { RFPRequirement } from '../types/source-grounded';

// ============================================================
// TYPES FOR RFP ANALYSIS INPUT
// ============================================================

/**
 * Analysis result from the analyze-rfp edge function
 */
interface RFPAnalysisResult {
  analysis?: {
    requirements?: string[];
    evaluationCriteria?: string[];
    compliance?: string[];
    certifications?: string[];
    riskFactors?: string[];
    opportunities?: string[];
    competitiveAdvantage?: string;
  };
  parsedSolicitation?: {
    sectionL?: {
      volumeRequirements?: Array<{
        volumeNumber?: string;
        title?: string;
        pageLimit?: number;
        requiredSections?: string[];
      }>;
    };
    sectionM?: {
      evaluationFactors?: Array<{
        factor?: string;
        weight?: string;
        description?: string;
        subfactors?: string[];
      }>;
    };
    sectionC?: {
      tasks?: Array<{
        taskNumber?: string;
        title?: string;
        description?: string;
        deliverables?: string[];
      }>;
      scopeSummary?: string;
    };
    sectionI?: {
      farClauses?: string[];
      dfarsClauses?: string[];
      specialProvisions?: string[];
    };
  };
  contractCharacteristics?: {
    contractType?: string;
    isDoD?: boolean;
    requiresClearance?: boolean;
    setAsideType?: string;
    requiresCostPricingData?: boolean;
  };
}

/**
 * Contract data for context
 */
interface ContractContext {
  title?: string;
  agency?: string;
  naicsCode?: string;
  setAsides?: string[];
  fundingAmount?: number;
  description?: string;
}

// ============================================================
// REQUIREMENT ID GENERATION
// ============================================================

/**
 * Counter state for ID generation
 */
interface IdCounters {
  technical: number;
  management: number;
  past_performance: number;
  personnel: number;
  pricing: number;
  compliance: number;
  other: number;
}

/**
 * Generate a unique requirement ID
 */
function generateRequirementId(
  category: RFPRequirement['category'],
  counters: IdCounters
): string {
  const prefixMap: Record<string, string> = {
    technical: 'REQ-TECH',
    management: 'REQ-MGMT',
    past_performance: 'REQ-PP',
    personnel: 'REQ-PERS',
    pricing: 'REQ-PRICE',
    compliance: 'REQ-COMP',
    other: 'REQ-GEN',
  };

  counters[category]++;
  const prefix = prefixMap[category] || 'REQ-GEN';
  return `${prefix}-${String(counters[category]).padStart(3, '0')}`;
}

// ============================================================
// KEYWORD EXTRACTION
// ============================================================

/**
 * Federal contracting keyword patterns for extraction
 */
const KEYWORD_PATTERNS = {
  technical: [
    /software\s+development/gi,
    /system\s+integration/gi,
    /cloud\s+(?:migration|services|computing)/gi,
    /cybersecurity|cyber\s+security/gi,
    /data\s+analytics/gi,
    /artificial\s+intelligence|ai|machine\s+learning|ml/gi,
    /devops|devsecops/gi,
    /agile|scrum|kanban/gi,
    /api\s+(?:development|integration)/gi,
    /microservices/gi,
    /containerization|docker|kubernetes/gi,
    /network\s+(?:engineering|administration|security)/gi,
    /helpdesk|service\s+desk/gi,
    /it\s+support/gi,
    /database\s+(?:administration|management)/gi,
    /automation/gi,
    /testing|qa|quality\s+assurance/gi,
    /modernization/gi,
    /legacy\s+system/gi,
  ],
  management: [
    /project\s+management/gi,
    /program\s+management/gi,
    /risk\s+management/gi,
    /change\s+management/gi,
    /configuration\s+management/gi,
    /quality\s+management/gi,
    /transition\s+plan/gi,
    /staffing\s+plan/gi,
    /communication\s+plan/gi,
    /governance/gi,
    /pmp|pmi/gi,
    /itil/gi,
    /status\s+report/gi,
    /milestone/gi,
    /deliverable/gi,
  ],
  personnel: [
    /key\s+personnel/gi,
    /project\s+manager/gi,
    /program\s+manager/gi,
    /technical\s+lead/gi,
    /senior\s+(?:developer|engineer|analyst)/gi,
    /subject\s+matter\s+expert|sme/gi,
    /clearance|security\s+clearance/gi,
    /top\s+secret|ts\/sci|secret/gi,
    /resume/gi,
    /qualifications/gi,
    /experience\s+requirements/gi,
    /certifications?\s+required/gi,
  ],
  past_performance: [
    /past\s+performance/gi,
    /relevant\s+experience/gi,
    /similar\s+(?:work|contracts?|projects?)/gi,
    /contract\s+references?/gi,
    /cpars/gi,
    /ppirs/gi,
    /performance\s+record/gi,
    /demonstrated\s+(?:experience|capability)/gi,
    /track\s+record/gi,
    /successful\s+(?:completion|delivery)/gi,
  ],
  pricing: [
    /price\s+proposal/gi,
    /cost\s+proposal/gi,
    /pricing\s+structure/gi,
    /labor\s+rates?/gi,
    /indirect\s+rates?/gi,
    /odc|other\s+direct\s+costs?/gi,
    /travel\s+costs?/gi,
    /materials?\s+costs?/gi,
    /ffp|firm\s+fixed\s+price/gi,
    /t&m|time\s+and\s+materials?/gi,
    /cost\s+realism/gi,
    /price\s+reasonableness/gi,
    /cost\s+accounting/gi,
    /incurred\s+cost/gi,
  ],
  compliance: [
    /far\s+\d{2}\.\d{3}/gi,
    /dfars?\s+\d{3}\.\d{3}/gi,
    /sam\.gov|sam\s+registration/gi,
    /duns|uei/gi,
    /representations?\s+and\s+certifications?/gi,
    /small\s+business|8\(a\)|hubzone|sdvosb|wosb/gi,
    /subcontracting\s+plan/gi,
    /organizational\s+conflict/gi,
    /oci/gi,
    /disclosure/gi,
    /insurance\s+requirements?/gi,
    /bonding/gi,
    /nist|sp\s+800-171|cmmc/gi,
    /fisma/gi,
    /fedramp/gi,
    /section\s+508/gi,
    /ada\s+compliance/gi,
  ],
};

/**
 * Extract keywords from requirement text
 */
function extractKeywords(text: string): string[] {
  const keywords: Set<string> = new Set();
  const normalizedText = text.toLowerCase();

  // Check all patterns
  for (const patterns of Object.values(KEYWORD_PATTERNS)) {
    for (const pattern of patterns) {
      const matches = text.match(pattern);
      if (matches) {
        matches.forEach(m => keywords.add(m.toLowerCase().trim()));
      }
    }
  }

  // Also extract capitalized terms (often technical terms)
  const capitalizedTerms = text.match(/\b[A-Z][A-Z0-9]{2,}\b/g);
  if (capitalizedTerms) {
    capitalizedTerms.forEach(term => {
      if (term.length <= 10 && !['THE', 'AND', 'FOR', 'ARE', 'NOT'].includes(term)) {
        keywords.add(term.toLowerCase());
      }
    });
  }

  return Array.from(keywords).slice(0, 15);
}

/**
 * Determine requirement category from text
 */
function categorizeRequirement(text: string): RFPRequirement['category'] {
  const normalizedText = text.toLowerCase();

  // Count matches for each category
  const categoryScores: Record<RFPRequirement['category'], number> = {
    technical: 0,
    management: 0,
    past_performance: 0,
    personnel: 0,
    pricing: 0,
    compliance: 0,
    other: 0,
  };

  for (const [category, patterns] of Object.entries(KEYWORD_PATTERNS)) {
    for (const pattern of patterns) {
      const matches = text.match(pattern);
      if (matches) {
        categoryScores[category as RFPRequirement['category']] += matches.length;
      }
    }
  }

  // Find highest scoring category
  let maxCategory: RFPRequirement['category'] = 'other';
  let maxScore = 0;

  for (const [category, score] of Object.entries(categoryScores)) {
    if (score > maxScore) {
      maxScore = score;
      maxCategory = category as RFPRequirement['category'];
    }
  }

  return maxCategory;
}

/**
 * Determine if a requirement is mandatory
 */
function isMandatory(text: string): boolean {
  const mandatoryIndicators = [
    /\bshall\b/i,
    /\bmust\b/i,
    /\brequired\b/i,
    /\bmandatory\b/i,
    /\bminimum\b/i,
    /\bpass\/fail\b/i,
    /\bgo\/no-go\b/i,
  ];

  return mandatoryIndicators.some(pattern => pattern.test(text));
}

// ============================================================
// MAIN EXTRACTION FUNCTIONS
// ============================================================

/**
 * Extract structured requirements from RFP analysis result
 */
export function extractStructuredRequirements(
  analysisResult: RFPAnalysisResult,
  contractContext?: ContractContext
): RFPRequirement[] {
  const requirements: RFPRequirement[] = [];
  const counters: IdCounters = {
    technical: 0,
    management: 0,
    past_performance: 0,
    personnel: 0,
    pricing: 0,
    compliance: 0,
    other: 0,
  };

  // 1. Extract from analysis.requirements
  if (analysisResult.analysis?.requirements) {
    for (const reqText of analysisResult.analysis.requirements) {
      if (!reqText || reqText.length < 10) continue;

      const category = categorizeRequirement(reqText);
      requirements.push({
        id: generateRequirementId(category, counters),
        text: reqText,
        source: 'RFP Analysis - Requirements',
        category,
        mandatory: isMandatory(reqText),
        keywords: extractKeywords(reqText),
      });
    }
  }

  // 2. Extract from evaluation criteria (Section M)
  if (analysisResult.analysis?.evaluationCriteria) {
    for (const criteriaText of analysisResult.analysis.evaluationCriteria) {
      if (!criteriaText || criteriaText.length < 10) continue;

      const category = categorizeRequirement(criteriaText);
      requirements.push({
        id: generateRequirementId(category, counters),
        text: criteriaText,
        source: 'Section M - Evaluation Criteria',
        category,
        mandatory: true, // Evaluation criteria are typically mandatory
        keywords: extractKeywords(criteriaText),
      });
    }
  }

  // 3. Extract from parsed Section M evaluation factors
  if (analysisResult.parsedSolicitation?.sectionM?.evaluationFactors) {
    for (const factor of analysisResult.parsedSolicitation.sectionM.evaluationFactors) {
      if (!factor.factor) continue;

      const factorText = factor.description
        ? `${factor.factor}: ${factor.description}`
        : factor.factor;

      const category = categorizeRequirement(factorText);

      // Parse weight if available
      let weight: number | undefined;
      if (factor.weight) {
        const weightMatch = factor.weight.match(/(\d+)/);
        if (weightMatch) {
          weight = parseInt(weightMatch[1]);
        }
      }

      requirements.push({
        id: generateRequirementId(category, counters),
        text: factorText,
        source: `Section M - Factor: ${factor.factor}`,
        category,
        mandatory: true,
        weight,
        keywords: extractKeywords(factorText),
      });

      // Also extract subfactors
      if (factor.subfactors) {
        for (const subfactor of factor.subfactors) {
          if (!subfactor || subfactor.length < 5) continue;

          const subCategory = categorizeRequirement(subfactor);
          requirements.push({
            id: generateRequirementId(subCategory, counters),
            text: subfactor,
            source: `Section M - Subfactor under ${factor.factor}`,
            category: subCategory,
            mandatory: true,
            keywords: extractKeywords(subfactor),
          });
        }
      }
    }
  }

  // 4. Extract from Section C tasks
  if (analysisResult.parsedSolicitation?.sectionC?.tasks) {
    for (const task of analysisResult.parsedSolicitation.sectionC.tasks) {
      if (!task.title && !task.description) continue;

      const taskText = task.description
        ? `${task.title || 'Task'}: ${task.description}`
        : task.title || '';

      if (taskText.length < 10) continue;

      requirements.push({
        id: generateRequirementId('technical', counters),
        text: taskText,
        source: `Section C - Task ${task.taskNumber || ''}`.trim(),
        category: 'technical',
        mandatory: true,
        keywords: extractKeywords(taskText),
      });

      // Extract deliverable requirements
      if (task.deliverables) {
        for (const deliverable of task.deliverables) {
          if (!deliverable || deliverable.length < 5) continue;

          requirements.push({
            id: generateRequirementId('technical', counters),
            text: `Deliverable: ${deliverable}`,
            source: `Section C - Deliverable for Task ${task.taskNumber || ''}`.trim(),
            category: 'technical',
            mandatory: true,
            keywords: extractKeywords(deliverable),
          });
        }
      }
    }
  }

  // 5. Extract from compliance requirements
  if (analysisResult.analysis?.compliance) {
    for (const compText of analysisResult.analysis.compliance) {
      if (!compText || compText.length < 10) continue;

      requirements.push({
        id: generateRequirementId('compliance', counters),
        text: compText,
        source: 'RFP Analysis - Compliance',
        category: 'compliance',
        mandatory: true, // Compliance is always mandatory
        keywords: extractKeywords(compText),
      });
    }
  }

  // 6. Extract from certification requirements
  if (analysisResult.analysis?.certifications) {
    for (const certText of analysisResult.analysis.certifications) {
      if (!certText || certText.length < 5) continue;

      requirements.push({
        id: generateRequirementId('compliance', counters),
        text: `Required Certification: ${certText}`,
        source: 'RFP Analysis - Certifications',
        category: 'compliance',
        mandatory: true,
        keywords: extractKeywords(certText),
      });
    }
  }

  // 7. Extract from contract characteristics
  if (analysisResult.contractCharacteristics) {
    const chars = analysisResult.contractCharacteristics;

    if (chars.requiresClearance) {
      requirements.push({
        id: generateRequirementId('personnel', counters),
        text: 'Security clearance required for key personnel',
        source: 'Contract Characteristics',
        category: 'personnel',
        mandatory: true,
        keywords: ['security clearance', 'clearance', 'classified'],
      });
    }

    if (chars.isDoD) {
      requirements.push({
        id: generateRequirementId('compliance', counters),
        text: 'DoD contract - DFARS compliance required',
        source: 'Contract Characteristics',
        category: 'compliance',
        mandatory: true,
        keywords: ['dod', 'dfars', 'defense'],
      });
    }

    if (chars.setAsideType) {
      requirements.push({
        id: generateRequirementId('compliance', counters),
        text: `Set-aside requirement: ${chars.setAsideType}`,
        source: 'Contract Characteristics',
        category: 'compliance',
        mandatory: true,
        keywords: [chars.setAsideType.toLowerCase(), 'set-aside', 'small business'],
      });
    }

    if (chars.requiresCostPricingData) {
      requirements.push({
        id: generateRequirementId('pricing', counters),
        text: 'Cost or pricing data required (FAR 15.403)',
        source: 'Contract Characteristics',
        category: 'pricing',
        mandatory: true,
        keywords: ['cost data', 'pricing data', 'far 15.403', 'truth in negotiations'],
      });
    }
  }

  // 8. Add past performance requirement (nearly always present)
  const hasPastPerformanceReq = requirements.some(
    r => r.category === 'past_performance'
  );
  if (!hasPastPerformanceReq) {
    requirements.push({
      id: generateRequirementId('past_performance', counters),
      text: 'Demonstrated past performance on similar contracts',
      source: 'Standard Requirement',
      category: 'past_performance',
      mandatory: true,
      keywords: ['past performance', 'relevant experience', 'similar work'],
    });
  }

  // Deduplicate by similar text
  const deduped = deduplicateRequirements(requirements);

  return deduped;
}

/**
 * Deduplicate requirements with similar text
 */
function deduplicateRequirements(requirements: RFPRequirement[]): RFPRequirement[] {
  const seen = new Map<string, RFPRequirement>();

  for (const req of requirements) {
    // Create normalized key for comparison
    const normalizedText = req.text
      .toLowerCase()
      .replace(/[^\w\s]/g, '')
      .split(/\s+/)
      .filter(w => w.length > 2)
      .sort()
      .join(' ');

    // Check if similar requirement already exists
    const key = normalizedText.substring(0, 100);

    if (!seen.has(key)) {
      seen.set(key, req);
    } else {
      // Keep the one with more keywords or from a better source
      const existing = seen.get(key)!;
      if (req.keywords.length > existing.keywords.length ||
          req.source.includes('Section') && !existing.source.includes('Section')) {
        seen.set(key, req);
      }
    }
  }

  return Array.from(seen.values());
}

/**
 * Extract requirements directly from raw solicitation text
 * Used when we don't have a pre-analyzed result
 */
export function extractRequirementsFromText(
  solicitationText: string,
  contractContext?: ContractContext
): RFPRequirement[] {
  const requirements: RFPRequirement[] = [];
  const counters: IdCounters = {
    technical: 0,
    management: 0,
    past_performance: 0,
    personnel: 0,
    pricing: 0,
    compliance: 0,
    other: 0,
  };

  // Pattern to find requirement-like sentences
  const requirementPatterns = [
    // "shall" requirements
    /(?:the\s+)?(?:contractor|offeror|vendor)\s+shall\s+([^.]+\.)/gi,
    // "must" requirements
    /(?:the\s+)?(?:contractor|offeror|vendor)\s+must\s+([^.]+\.)/gi,
    // "required to" requirements
    /(?:is|are)\s+required\s+to\s+([^.]+\.)/gi,
    // Deliverable patterns
    /deliverable[s]?\s*[:]\s*([^.]+\.)/gi,
    // Factor patterns
    /factor\s+\d+[:\s]+([^.]+\.)/gi,
    // Task descriptions
    /task\s+\d+[.:]?\d*[:\s]+([^.]+\.)/gi,
  ];

  for (const pattern of requirementPatterns) {
    const matches = solicitationText.matchAll(pattern);
    for (const match of matches) {
      const reqText = match[1] || match[0];
      if (reqText.length < 20 || reqText.length > 500) continue;

      const category = categorizeRequirement(reqText);
      requirements.push({
        id: generateRequirementId(category, counters),
        text: reqText.trim(),
        source: 'Extracted from Solicitation Text',
        category,
        mandatory: isMandatory(reqText),
        keywords: extractKeywords(reqText),
      });
    }
  }

  return deduplicateRequirements(requirements);
}

/**
 * Merge requirements from multiple sources
 */
export function mergeRequirements(
  ...requirementSets: RFPRequirement[][]
): RFPRequirement[] {
  const allRequirements = requirementSets.flat();

  // Re-assign IDs to ensure uniqueness
  const counters: IdCounters = {
    technical: 0,
    management: 0,
    past_performance: 0,
    personnel: 0,
    pricing: 0,
    compliance: 0,
    other: 0,
  };

  const merged = deduplicateRequirements(allRequirements);

  // Reassign sequential IDs
  for (const req of merged) {
    req.id = generateRequirementId(req.category, counters);
  }

  return merged;
}

/**
 * Get summary statistics for requirements
 */
export function getRequirementsSummary(requirements: RFPRequirement[]): {
  total: number;
  mandatory: number;
  byCategory: Record<string, number>;
  weightedCount: number;
} {
  const byCategory: Record<string, number> = {};
  let mandatory = 0;
  let weightedCount = 0;

  for (const req of requirements) {
    byCategory[req.category] = (byCategory[req.category] || 0) + 1;
    if (req.mandatory) mandatory++;
    if (req.weight !== undefined) weightedCount++;
  }

  return {
    total: requirements.length,
    mandatory,
    byCategory,
    weightedCount,
  };
}
